import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { CHANNELS } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", service_id: "", channel: "", message: "" };

export const NewLeadDialog = ({ open, onOpenChange, services, onDone }) => {
  const navigate = useNavigate();
  const [v, setV] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const valid = v.first_name.trim() && v.phone.trim() && v.channel;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/leads", { ...v, email: v.email || null, service_id: v.service_id || null });
      toast.success(data.message);
      setV(EMPTY);
      onOpenChange(false);
      onDone();
      if (data.lead) navigate(`/crm/leads/${data.lead.id}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input value={v[k]} onChange={(e) => set(k)(e.target.value)} className="mt-1.5 h-10 rounded-xl" data-testid={`new-lead-${k}`} {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="new-lead-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold">Nuevo lead</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          {field("first_name", "Nombre *")}
          {field("last_name", "Apellido")}
          {field("phone", "Teléfono *", { type: "tel" })}
          {field("email", "Correo", { type: "email" })}
          <div>
            <Label className="text-xs font-semibold text-slate-600">Canal de origen *</Label>
            <Select value={v.channel || undefined} onValueChange={set("channel")}>
              <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="new-lead-channel"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Servicio</Label>
            <Select value={v.service_id || undefined} onValueChange={set("service_id")}>
              <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="new-lead-service"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Mensaje / nota inicial</Label>
            <Textarea rows={3} value={v.message} onChange={(e) => set("message")(e.target.value)} className="mt-1.5 rounded-xl" data-testid="new-lead-message" />
          </div>
          {error && <p className="text-sm text-rose-600 sm:col-span-2" data-testid="new-lead-error">{error}</p>}
          <Button type="submit" disabled={!valid || saving} className="rounded-xl bg-[#1E6FD9] sm:col-span-2" data-testid="new-lead-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear lead"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
